/**
 * AMP3 product-page bundles (sections/amp3-bundles.liquid).
 *
 * Kaching Bundles renders its widget inside the section's product form and remains the source
 * of truth for offers, variants, discounts and the basket add. This script only presents it:
 *   - shows the section when Kaching renders at least one product-bundle bar (else stays hidden)
 *   - builds a tab per bundle bar, in the deal's order; a tab selects that bar in Kaching
 *
 * It uses Kaching's supported API (confirmed by Kaching support, 27 Sep 2026) wherever it can:
 * the deal settings JSON for bars/products, selectedDealBarId to select, pricing() for the
 * bundle price, items() for what goes in the basket, and its events to re-render. Kaching's
 * markup is only used for the product cards this restyles and the unselected tabs' prices.
 *   - enriches Kaching's product cards (sharper image, eyebrow, short description)
 *   - makes the cards links: extras open their product page (same tab), this product scrolls to its gallery
 *   - fills the summary: Kaching's bundle price, "bought separately today" from Shopify's current
 *     prices, and the difference as the saving
 *   - blocks the bundle button when the chosen main variant or an accessory isn't in stock
 *   - an extra that's out of stock says so on its card, the tab and the button; with an arrival date (and Continue
 *     selling) the bundle can still be ordered and says when the extra is due; a bundle that can't be bought or
 *     ordered is never opened first, and with none left the section and its teaser stay hidden (Zach 14 Oct,
 *     Joshua 15 Oct 2026)
 *   - adds a "Better together" link under the main Add to Basket
 *   - from the basket's "Goes well with" (amp3-basket.js): ?a3bar= opens that bar and scrolls here;
 *     ?a3swap=<variant> takes the customer's separate item out of the basket once the bundle is added
 */
(function () {
  const root = document.querySelector('[data-a3-bundles]');
  if (!root) return;   // no bundle deal for this product (the section removed itself)
  const widget = root.querySelector('kaching-bundle');
  if (!widget) return;
  const form = root.querySelector('form');
  const $ = (sel) => root.querySelector(sel);
  const stock = JSON.parse(root.querySelector('[data-a3-bundle-stock]').textContent || '{}');
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  const money = (pence) => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(pence / 100);
  const cards = {};   // handle -> Promise<card JSON>
  // From the basket's "Goes well with": read once, as the variant picker rewrites the URL on a colour change.
  const fromBasket = new URLSearchParams(window.location.search);
  const wantBar = fromBasket.get('a3bar');
  let swapVariant = fromBasket.get('a3swap');
  let lastSig = '';
  let status = null;   // bundle id -> { kind: 'ok' | 'dated' | 'out', name, due }, from statuses() at start

  // Kaching's deal settings (inline JSON on the page): bar ids, titles and products, in the deal's order.
  const settings = (() => {
    for (const s of document.querySelectorAll(`script.kaching-bundles-deal-block-settings[data-product-id="${root.dataset.productId}"]`)) {
      try { const d = JSON.parse(s.textContent); if (d.dealBars.some((b) => b.dealBarType === 'bundle')) return d; } catch (e) { /* ignore */ }
    }
    return null;
  })();
  if (!settings) { root.remove(); return; }
  const bundleDefs = settings.dealBars.filter((b) => b.dealBarType === 'bundle');

  function card(handle) {
    if (!cards[handle]) cards[handle] = fetch(`/products/${handle}?view=bundle-card`).then((r) => r.json()).catch(() => null);
    return cards[handle];
  }
  const allCards = () => Promise.all([root.dataset.productHandle, ...(settings.otherProductHandles || [])].map(card));

  // Kaching's supported widget API lives on its <kaching-bundles-block> element:
  // selectedDealBarId (get/set), pricing(), items(); events deal-bar-selected, items-changed.
  const api = () => widget.querySelector('kaching-bundles-block');
  const call = (fn) => { try { const a = api(); return a && typeof a[fn] === 'function' ? a[fn]() : null; } catch (e) { return null; } };
  const barEl = (id) => root.querySelector(`.kaching-bundles__bar[data-deal-bar-id="${CSS.escape(id)}"]`);
  const bundleBars = () => bundleDefs.filter((d) => barEl(d.id));
  const selectedId = () => { const a = api(); return a && a.selectedDealBarId; };
  const select = (id) => {
    const a = api();
    if (a && 'selectedDealBarId' in a) a.selectedDealBarId = id;
    else { const l = barEl(id) && barEl(id).querySelector('label'); if (l) l.click(); }   // fallback: click Kaching's bar
  };
  const text = (el, sel) => ((el && el.querySelector(sel)) || {}).textContent?.trim() || '';
  const barTitle = (d) => (d.title && !d.title.includes('{{') ? d.title : text(barEl(d.id), '.kaching-bundles__bar-title'));

  /** Kaching's product cards in a bar, paired with the deal's product (same order): productGID "default" is this product. */
  function products(def) {
    const els = [...barEl(def.id).querySelectorAll('.kaching-bundles__bundle-products__product')];
    return els.map((el, i) => {
      const bp = def.bundleProducts[i] || {};
      const main = !bp.productGID || bp.productGID === 'default';
      const productId = main ? Number(root.dataset.productId) : Number(String(bp.productGID).split('/').pop());
      const titleEl = el.querySelector('.kaching-bundles__bundle-products__title span') || el.querySelector('.kaching-bundles__bundle-products__title');
      return { el, main, productId, titleEl, title: titleEl ? (titleEl.dataset.a3Full || titleEl.textContent.trim()) : '' };
    });
  }

  const inStock = (v) => v && (v.tracked === null || v.tracked === '' || v.qty > 0) && v.available;
  /** Out of stock, but on Continue selling with an arrival date still to come (bundle-card "due"): it can be ordered. */
  const dated = (v) => !!(v && !inStock(v) && v.available && v.due);
  /** The finishes a bundle offers for an extra (Kaching can limit them), from its card. */
  const offered = (bp, c) => {
    const ids = (bp.variantGIDs || []).map((g) => Number(String(g).split('/').pop()));
    return ids.length ? c.variants.filter((v) => ids.includes(v.id)) : c.variants;
  };
  const mainOf = (item) => { try { return JSON.parse(item.properties.__kaching_bundles).main === true; } catch (e) { return false; } };

  /** "Leather Case for the FiiO CP13" -> "Leather Case"; "FiiO JH13 Hybrid In-Ear…" -> "JH13 Hybrid In-Ear" */
  function shortName(title) {
    let t = title.split(/\s+for\s+(the\s+)?/i)[0];
    t = t.replace(/^(fiio|shanling|ibasso|cayin|hifiman|astell\s*&\s*kern|dunu|jade audio|snowsky)\s+/i, '');
    return t.split(/\s+/).slice(0, 3).join(' ');
  }

  /** A short name for an extra in stock messages: "Shanling ME600 Hybrid In Ear Monitors" -> "ME600 Hybrid". */
  const nameOf = (c) => shortName(c.title.replace(new RegExp('^' + (c.vendor || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i'), '')).replace(/\s+\S{1,2}$/, '');

  /* ---------- tabs ---------- */
  function renderTabs(defs, currentId, currentPrice) {
    const wrap = $('[data-a3-bundle-tabs]');
    const hadFocus = wrap.contains(document.activeElement);   // keep keyboard focus across re-renders
    wrap.hidden = defs.length < 2;
    wrap.innerHTML = defs.map((d, i) => {
      const on = d.id === currentId;
      const items = products(d).map((p) => p.main ? root.dataset.shortName : shortName(p.title)).join(' + ');
      const price = on && currentPrice ? currentPrice : text(barEl(d.id), '.kaching-bundles__bar-price');
      const st = status && status[d.id];
      const chip = st && st.kind === 'out' ? '<span class="a3-bundles__tab-stock is-out">Out of stock</span>'
        : st && st.kind === 'dated' ? `<span class="a3-bundles__tab-stock is-due">Due ${st.due}</span>` : '';
      return `<button type="button" role="tab" class="a3-bundles__tab" aria-selected="${on}" tabindex="${on ? 0 : -1}" data-bar="${d.id}">
        <span class="a3-bundles__tab-n">${String(i + 1).padStart(2, '0')}</span>
        <span class="a3-bundles__tab-text"><strong>${barTitle(d)}</strong><small>${items}</small>${chip}</span>
        <span class="a3-bundles__tab-price">${price}</span></button>`;
    }).join('');
    if (hadFocus) { const t = wrap.querySelector('[aria-selected="true"]'); if (t) t.focus(); }
  }

  /**
   * An accessory whose chosen finish is out of stock switches to an in-stock finish through Kaching's
   * own option menu (as if the customer chose it), once per bar and product. Single-option products only.
   */
  const autoSwitched = new Set();
  function inStockFinish(def, list, c, v) {
    const key = def.id + ':' + c.id;
    if (autoSwitched.has(key)) return false;
    autoSwitched.add(key);
    const p = list.find((x) => x.productId === c.id);
    const selects = p ? p.el.querySelectorAll('select.kaching-bundles__bar-variant-select') : [];
    if (selects.length !== 1) return false;
    const sel = selects[0];
    const listed = (x) => x.id !== v.id && [...sel.options].some((o) => o.value.toLowerCase() === x.title.toLowerCase());
    const alt = c.variants.find((x) => listed(x) && inStock(x)) || (!dated(v) && c.variants.find((x) => listed(x) && dated(x)));
    if (!alt) return false;
    sel.value = [...sel.options].find((o) => o.value.toLowerCase() === alt.title.toLowerCase()).value;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  /**
   * Each bundle: 'ok' (every extra has a finish in stock), 'dated' (an extra can only be ordered ahead, with a date:
   * the latest one is shown) or 'out' (an extra has no finish in stock and no date). A card that didn't load counts
   * as ok, so a network hiccup never hides a bundle.
   */
  async function statuses(defs) {
    const byId = {};
    (await allCards()).filter(Boolean).forEach((c) => { byId[c.id] = c; });
    const out = {};
    defs.forEach((d) => {
      const st = { kind: 'ok', name: '', due: '', dueIso: '' };
      (d.bundleProducts || []).forEach((bp) => {
        if (!bp.productGID || bp.productGID === 'default') return;
        const c = byId[Number(String(bp.productGID).split('/').pop())];
        if (!c) return;
        const vs = offered(bp, c);
        if (vs.some(inStock)) return;
        const soon = vs.filter(dated).sort((a, b) => a.due.localeCompare(b.due))[0];
        if (!soon) { st.kind = 'out'; st.name = nameOf(c); return; }
        if (st.kind === 'out') return;
        st.kind = 'dated';
        if (soon.due > st.dueIso) { st.dueIso = soon.due; st.due = soon.dueText; st.name = nameOf(c); }
      });
      out[d.id] = st;
    });
    return out;
  }
  /** The bundle to open on: the first that can be bought now, else the first that can be ordered ahead, else none. */
  const pick = (defs) => {
    const k = (d) => (status && status[d.id] ? status[d.id].kind : 'ok');
    return (defs.find((d) => k(d) === 'ok') || defs.find((d) => k(d) === 'dated') || {}).id || null;
  };

  /* ---------- cards + summary ---------- */
  async function render() {
    const defs = bundleBars();
    if (!defs.length) return;
    const id = selectedId();
    const def = defs.find((d) => d.id === id);
    if (!def) { const id = pick(defs); if (id) select(id); return; }   // Kaching preselected the standard bar: move to the best bundle

    const list = products(def);
    const items = call('items') || [];
    const pricing = call('pricing');
    const total = pricing && pricing.discountedPrice ? Math.round(pricing.discountedPrice.amount * 100) : null;
    const totalText = pricing && pricing.discountedPrice ? pricing.discountedPrice.formatted : text(barEl(def.id), '.kaching-bundles__bar-price');
    const sig = def.id + '|' + items.map((it) => it.id + 'x' + it.quantity).join(',') + '|' + totalText;
    const missing = list.some((p) => !p.el.querySelector('.a3-bcard'));   // Kaching re-rendered a card
    if (sig === lastSig && !missing) return;
    lastSig = sig;

    renderTabs(defs, def.id, totalText);
    const data = (await allCards()).filter(Boolean);
    const byVariant = {};
    data.forEach((c) => c.variants.forEach((v) => { byVariant[v.id] = { c, v }; }));
    const byProduct = {};
    data.forEach((c) => { byProduct[c.id] = c; });

    // What Kaching will add to the basket: current Shopify prices ("bought separately") and stock.
    let separate = 0, known = items.length > 0, blocked = '', blockedLabel = '', later = null;
    const chosen = {};   // product id -> variant being added (for the card image)
    const cardState = {};   // product id -> { kind: 'out' | 'due', text } for an extra that isn't in stock
    let switched = false;
    items.forEach((it) => {
      const hit = byVariant[it.id];
      if (!hit) { known = false; return; }
      separate += hit.v.price * (it.quantity || 1);
      chosen[hit.c.id] = hit.v;
      if (mainOf(it) || hit.c.id === Number(root.dataset.productId)) {
        const s = stock[it.id];
        if (s && s.tracked && !(s.qty > 0)) { blocked = root.dataset.unavailable; blockedLabel = 'Currently unavailable'; }
      } else if (!inStock(hit.v)) {
        if (inStockFinish(def, list, hit.c, hit.v)) { switched = true; return; }   // Kaching re-renders with it
        const name = nameOf(hit.c);
        const finish = hit.v.title !== 'Default Title' ? ` (${hit.v.title.charAt(0) + hit.v.title.slice(1).toLowerCase()})` : '';
        if (dated(hit.v)) {
          // Continue selling with a date: it can be ordered; say when it's due.
          if (!later || hit.v.due > later.dueIso) later = { name, due: hit.v.dueText, dueIso: hit.v.due };
          cardState[hit.c.id] = { kind: 'due', text: `Out of stock · due ${hit.v.dueText}` };
        } else {
          cardState[hit.c.id] = { kind: 'out', text: 'Out of stock' };
          if (!blocked) {
            blocked = `The ${name}${finish} is out of stock and we don't have a date for it yet. Choose another finish or bundle, or buy the ${root.dataset.shortName} on its own.`;
            blockedLabel = `${name} out of stock`;
          }
        }
      }
    });
    if (switched) { lastSig = ''; setTimeout(render, 600); return; }

    // Enrich Kaching's product cards (re-applied whenever Kaching re-renders them)
    list.forEach((p, i) => {
      const c = byProduct[p.productId];
      if (!c) return;
      const v = chosen[c.id];
      const img = p.el.querySelector('img.kaching-bundles__bundle-products__image');
      const src = (v && v.image) || c.image;
      if (img && src && img.dataset.a3 !== src) { img.src = src; img.removeAttribute('srcset'); img.dataset.a3 = src; img.alt = c.title; }
      let meta = p.el.querySelector('.a3-bcard');
      if (!meta) { meta = document.createElement('div'); meta.className = 'a3-bcard'; p.el.prepend(meta); }
      meta.textContent = `${String(i + 1).padStart(2, '0')} / ${p.main ? 'Your ' + (c.label || 'pick') : 'Your extra'}`.toUpperCase();
      let sub = p.el.querySelector('.a3-bcard__sub');
      const title = p.el.querySelector('.kaching-bundles__bundle-products__title');
      if (!sub && title) { sub = document.createElement('p'); sub.className = 'a3-bcard__sub'; title.after(sub); }
      if (sub) sub.textContent = c.subtitle || '';
      // Stock chip on an extra that isn't in stock (Kaching's own card still shows its price as if it were).
      const st = p.main ? null : cardState[c.id];
      let chip = p.el.querySelector('.a3-bstock');
      if (st) {
        if (!chip && title) { chip = document.createElement('p'); title.after(chip); }
        if (chip) { chip.className = `a3-bstock is-${st.kind}`; if (chip.textContent !== st.text) chip.textContent = st.text; }
      } else if (chip) chip.remove();
      p.el.classList.toggle('a3-bcard-out', !!(st && st.kind === 'out'));
      // Kaching links the extras to their pages; this product's own image/title become links back up to its gallery.
      if (p.main) p.el.querySelectorAll('div.kaching-bundles__bundle-products__link').forEach((l) => {
        l.setAttribute('role', 'link');
        l.tabIndex = l.querySelector('img') ? -1 : 0;   // one keyboard stop per card (the title)
        l.title = `Back to the ${root.dataset.shortName} photos`;
      });
      let brand = p.el.querySelector('.a3-bcard__brand');
      if (!brand && title) { brand = document.createElement('span'); brand.className = 'a3-bcard__brand'; title.before(brand); }
      if (brand) brand.textContent = c.vendor || '';
      // Title without the brand, which now sits above it ("FiiO CP13 Cassette Player" -> "CP13 Cassette Player")
      if (p.titleEl && c.vendor) {
        const full = p.titleEl.dataset.a3Full || p.titleEl.textContent.trim();
        p.titleEl.dataset.a3Full = full;
        const short = full.replace(new RegExp('^' + c.vendor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i'), '');
        if (p.titleEl.textContent !== short) p.titleEl.textContent = short;
      }
    });

    $('[data-a3-bundle-name]').textContent = barTitle(def);
    $('[data-a3-total]').textContent = total !== null ? money(total) : totalText;
    const saving = known && total !== null ? separate - total : 0;
    $('[data-a3-separate-row]').hidden = !(known && saving > 0);
    $('[data-a3-saving-row]').hidden = !(known && saving > 0);
    $('[data-a3-separate]').textContent = money(separate);
    $('[data-a3-saving]').textContent = '−' + money(saving);
    const badge = $('[data-a3-bundle-badge]');
    badge.hidden = !(saving > 0);
    badge.textContent = `Save ${money(saving)} together`;

    const btn = $('[data-a3-bundle-add]');
    const msg = $('[data-a3-bundle-unavailable]');
    const label = btn.querySelector('span');
    if (label && !btn.dataset.label) btn.dataset.label = label.textContent;
    btn.disabled = !!blocked;
    btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    // A dated extra: "… · HE1000 due 16 Nov" (the model number when the name has one). Plain text only: Kaching writes its
    // price into any element inside the button. Non-breaking spaces keep "HE1000 due 16 Nov" together on a phone.
    const tag = later ? (later.name.split(' ').find((w) => /\d/.test(w)) || later.name) : '';
    if (label) label.textContent = blocked ? blockedLabel : later ? `${btn.dataset.label} · ${`${tag} due ${later.due}`.replace(/ /g, '\u00a0')}` : btn.dataset.label;
    const info = !blocked && later ? `The ${later.name} is due ${later.due}. You can order the bundle now and we'll send it as soon as it arrives.` : '';
    msg.hidden = !(blocked || info);
    msg.classList.toggle('is-info', !blocked && !!info);
    msg.textContent = blocked || info;
  }

  /* ---------- wiring ---------- */
  function teaser(count) {
    const tpl = document.querySelector('template[data-a3-bundle-teaser]');
    const mainForm = document.querySelector('form.js-product-form-main');
    if (!tpl || !mainForm || document.querySelector('.a3-bundle-teaser')) return;
    const el = tpl.content.firstElementChild.cloneNode(true);
    el.querySelector('span').textContent = el.querySelector('span').textContent.replace('[count]', count).replace(/1 bundle options/, '1 bundle option');
    // Under the delivery notes that follow the form (on demand, stock arrival, "Order by 2pm today" promise,
    // pickup), so the delivery line sits between Add to Basket and Better together (Joshua, 15 Oct 2026).
    let anchor = mainForm.closest('product-form') || mainForm;
    while (anchor.nextElementSibling && (anchor.nextElementSibling.matches('.a3-ondemand-note') || anchor.nextElementSibling.hasAttribute('data-dynamic-variant-content'))) anchor = anchor.nextElementSibling;
    anchor.after(el);
  }

  let started = false;
  async function start() {
    if (started) return;
    started = true;
    const defs = bundleBars();
    if (!defs.length) { root.remove(); return; }   // no bundle for this product: no section, no teaser
    status = await statuses(defs);
    const usable = defs.filter((d) => status[d.id].kind !== 'out');
    if (!usable.length) { root.remove(); return; }   // nothing can be bought or ordered ahead: no section, no teaser
    root.hidden = false;
    root.classList.add('is-ready');
    teaser(usable.length);
    if (wantBar && defs.some((d) => d.id === wantBar)) {
      select(wantBar);
      setTimeout(() => root.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } else {
      // Kaching's preselected bundle ("Selected by default") may not be buyable: open on the best one.
      const best = pick(defs), now = selectedId();
      if (best && best !== now && (!defs.some((d) => d.id === now) || status[now].kind !== 'ok')) select(best);
    }
    render();
    let t;
    const later = () => { clearTimeout(t); t = setTimeout(render, 60); };
    ['items-changed', 'deal-bar-selected', 'variant-selected'].forEach((n) => document.addEventListener(n, (e) => { if (widget.contains(e.target)) later(); }, true));
    // Kaching can re-render its cards without an event; re-apply the card styling when it does.
    new MutationObserver(later).observe(widget, { subtree: true, childList: true });
    form.addEventListener('change', later);
  }

  $('[data-a3-bundle-tabs]').addEventListener('click', (e) => {
    const tab = e.target.closest('[data-bar]');
    if (tab) select(tab.dataset.bar);
  });
  $('[data-a3-bundle-tabs]').addEventListener('keydown', (e) => {   // arrow keys / Home / End between tabs
    const tabs = [...root.querySelectorAll('.a3-bundles__tab')];
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    const next = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    const t = tabs[(next + tabs.length) % tabs.length];
    t.focus(); t.click();
  });
  // Product cards: an extra opens its product page in the same tab (Kaching's links use a new tab;
  // modifier clicks still open one), this product's card scrolls back up to the gallery.
  const toGallery = () => (document.getElementById('product-media') || document.body).scrollIntoView({ behavior: 'smooth', block: 'start' });
  root.addEventListener('click', (e) => {
    const link = e.target.closest('.kaching-bundles__bundle-products__link');
    if (!link) return;
    if (link.href) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      e.preventDefault();
      location.href = link.href;
    } else if (link.getAttribute('role') === 'link') {
      e.preventDefault();
      toGallery();
    }
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches && e.target.matches('[role="link"].kaching-bundles__bundle-products__link')) { e.preventDefault(); toGallery(); }
  });
  // "Make it a bundle" from the basket: the bundle replaces the separate item, so take one of it out
  // (the same variant if there is one, else the same product), once.
  async function swapOut() {
    if (!swapVariant) return;
    const variant = swapVariant;
    swapVariant = null;
    try {
      const cart = await fetch(((window.theme && theme.routes && theme.routes.cart) || '/cart') + '.js').then((r) => r.json());
      const plain = cart.items.filter((i) => i.product_id === Number(root.dataset.productId) && !(i.properties && i.properties.__kaching_bundles));
      const line = plain.find((i) => String(i.variant_id) === variant) || plain[0];
      if (!line) return;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity - 1 })
      });
      // After the theme's own drawer render (700 ms after the add).
      setTimeout(() => document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true })), 800);
    } catch (e) { /* the basket still has both; the customer can remove one */ }
  }
  ['on:cart:add', 'on:line-item:change'].forEach((n) => root.addEventListener(n, swapOut));

  $('[data-a3-bundle-solo]').addEventListener('click', (e) => {
    const main = document.querySelector('form.js-product-form-main');
    if (!main) return;
    e.preventDefault();
    main.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const b = main.querySelector('[name=add]'); if (b) setTimeout(() => b.focus({ preventScroll: true }), 400);
  });

  // Kaching loads asynchronously: start on its loaded event (or its loaded block, if that came first);
  // give up quietly after 15 s.
  const ready = () => { const a = api(); return a && typeof a.items === 'function' && barEl(bundleDefs[0].id); };
  if (ready()) start();
  else {
    const go = () => { if (ready()) { mo.disconnect(); start(); } };
    const mo = new MutationObserver(go);
    mo.observe(widget, { subtree: true, childList: true, attributes: true });
    document.addEventListener('kaching-bundles-block-loaded', () => setTimeout(go, 0));
    setTimeout(() => { if (!started) { mo.disconnect(); root.remove(); } }, 15000);
  }
})();
